/**
 * Product Card Variant Selector Handler
 * Updates price, compare-at-price, and discount badge when variant changes
 */

class ProductCardVariantHandler {
  constructor() {
    this.init();
  }

  init() {
    // Listen for custom variant:change events from global.js
    document.addEventListener('variant:change', (e) => {
      if (e.detail) {
        const card = e.target.closest('.card-wrapper');
        if (card) {
          this.handleVariantChangeFromEvent(e.detail, card);
        }
      }
    });
    
    // Find all complementary variant selects (backup)
    const variantSelectors = document.querySelectorAll('complementary-variant-selects');
    
    variantSelectors.forEach(selector => {
      const inputs = selector.querySelectorAll('input[type="radio"]');
      
      inputs.forEach(input => {
        input.addEventListener('change', (e) => {
          this.handleVariantChange(e.target, selector);
        });
      });
    });
  }

  handleVariantChangeFromEvent(detail, card) {
    const price = detail.price;
    const comparePrice = detail.comparePrice;
    
    // Update discount badge
    this.updateDiscountBadgeFromPrices(card, price, comparePrice);
    
    // Update price display in the price component (if not handled by global.js)
    this.updatePriceDisplayFromPrices(card, price, comparePrice);
  }

  handleVariantChange(input, selector) {
    const price = input.getAttribute('data-price');
    const comparePrice = input.getAttribute('data-compare-price');
    const productId = input.name.replace('variant-select-', '');
    
    // Find the card wrapper
    const card = input.closest('.card-wrapper');
    if (!card) return;

    // Update price display in the price component
    this.updatePriceDisplay(card, price, comparePrice);
    
    // Update discount badge
    this.updateDiscountBadge(card, price, comparePrice);
    
    // Update image if data-image exists
    const imageUrl = input.getAttribute('data-image');
    if (imageUrl) {
      this.updateProductImage(card, imageUrl);
    }
  }

  updatePriceDisplayFromPrices(card, price, comparePrice) {
    const priceValue = this.parsePriceString(price);
    const comparePriceValue = comparePrice ? this.parsePriceString(comparePrice) : 0;
    
    this.updatePriceDisplay(card, price, comparePrice);
    this.updateDiscountBadgeFromPrices(card, price, comparePrice);
  }

  updateDiscountBadgeFromPrices(card, price, comparePrice) {
    const priceValue = this.parsePriceString(price);
    const comparePriceValue = comparePrice ? this.parsePriceString(comparePrice) : 0;
    
    this.updateDiscountBadge(card, price, comparePrice);
  }

  updatePriceDisplay(card, price, comparePrice) {
    // Find price container
    const priceContainer = card.querySelector('.price');
    if (!priceContainer) return;

    // Check if compare price exists and is different
    const priceValue = this.parsePriceString(price);
    const comparePriceValue = comparePrice ? this.parsePriceString(comparePrice) : 0;
    
    if (comparePriceValue > priceValue) {
      // Show sale price with compare at price
      priceContainer.classList.add('price--on-sale');
      priceContainer.classList.remove('price--sold-out');
      
      // Update price__sale div
      let saleDiv = priceContainer.querySelector('.price__sale');
      if (saleDiv) {
        // Find the <s> tag with compare price (it's wrapped in a span)
        const compareSpan = saleDiv.querySelector('s.price-item--regular');
        // Find the sale price span (direct child, not in <s>)
        const priceSpan = saleDiv.querySelector('span.price-item--sale');
        
        if (compareSpan) compareSpan.textContent = comparePrice;
        if (priceSpan) priceSpan.textContent = price;
      }
      
      // Hide regular price
      const regularDiv = priceContainer.querySelector('.price__regular');
      if (regularDiv) {
        regularDiv.style.display = 'none';
      }
      if (saleDiv) {
        saleDiv.style.display = 'flex';
      }
    } else {
      // Show regular price
      priceContainer.classList.remove('price--on-sale');
      
      const regularDiv = priceContainer.querySelector('.price__regular');
      const saleDiv = priceContainer.querySelector('.price__sale');
      
      if (regularDiv) {
        const priceSpan = regularDiv.querySelector('.price-item--regular');
        if (priceSpan) priceSpan.textContent = price;
        regularDiv.style.display = 'block';
      }
      
      if (saleDiv) {
        saleDiv.style.display = 'none';
      }
    }
  }

  updateDiscountBadge(card, price, comparePrice) {
    // Find or create badge
    let badge = card.querySelector('.discount-badge');
    
    const priceValue = this.parsePriceString(price);
    const comparePriceValue = comparePrice ? this.parsePriceString(comparePrice) : 0;
    
    if (comparePriceValue > priceValue) {
      // Calculate discount percentage
      const discount = Math.round(((comparePriceValue - priceValue) / comparePriceValue) * 100);
      
      if (badge) {
        badge.textContent = `-${discount}%`;
        badge.style.display = 'flex';
      } else {
        // Create badge if it doesn't exist
        this.createDiscountBadge(card, discount);
      }
    } else {
      // Hide badge if no discount
      if (badge) {
        badge.style.display = 'none';
      }
    }
  }

  createDiscountBadge(card, discount) {
    const mediaLink = card.querySelector('.card__media');
    if (!mediaLink) return;

    // Get badge style from settings (check if there's a data attribute or use default)
    const badgeStyle = document.body.dataset.discountBadgeStyle || 'circle';
    
    const badge = document.createElement('div');
    badge.className = `discount-badge discount-badge--${badgeStyle}`;
    badge.textContent = `-${discount}%`;
    badge.setAttribute('data-discount', discount);
    
    mediaLink.appendChild(badge);
  }

  updateProductImage(card, imageUrl) {
    const img = card.querySelector('.card__media img');
    if (img && imageUrl) {
      img.src = imageUrl;
      
      // Update srcset if needed
      const width = img.width || 533;
      img.srcset = imageUrl;
    }
  }

  parsePriceString(priceStr) {
    if (!priceStr) return 0;
    
    // Remove currency symbols and convert to number
    // Handle formats like "159 €", "€159", "$159.00", etc.
    const cleaned = priceStr.replace(/[^\d.,]/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductCardVariantHandler();
  });
} else {
  new ProductCardVariantHandler();
}

// Re-initialize when new products are loaded (e.g., via AJAX)
document.addEventListener('shopify:section:load', () => {
  new ProductCardVariantHandler();
});
